/**
 * Highcharts option factory for the "Valuation vs. Subsequent Returns"
 * card's scatter + regression-fit chart. Deliberately a plain (non-stock)
 * chart — the x-axis is a numeric forward multiple, not a timeline — so this
 * doesn't reuse chartConfig.js's stockChart-shaped options; it follows the
 * same option-factory pattern instead (see TrendingPanel.js's Sparkline for
 * another component-local, non-stock Highcharts usage in this codebase).
 */

import { ratioName, yieldName, isYieldMetric } from './metricsConfig';

const MARKER_COLOR = '#4a90e2';
const CURRENT_POINT_COLOR = '#c0392b';

/**
 * True when the viewport is short and wide the way a phone in landscape (or
 * a small landscape tablet) is, as opposed to merely narrow (a portrait
 * phone, already caught by a plain `maxWidth` condition). Guarded for
 * environments without `window` (SSR/tests).
 */
const isShortLandscapeViewport = () => (
  typeof window !== 'undefined' && window.innerHeight <= 500
);

/**
 * The regression-fit line, drawn as exactly two points spanning the plotted
 * points' [min(forwardMultiple), max(forwardMultiple)] range — extended to
 * reach `currentMultiple` when the "today" point falls outside that range,
 * so the line visually connects to the current-valuation marker (which sits
 * exactly on the fit at `slope * currentMultiple + intercept`). Without this
 * the line would stop at the historical cluster and the current point would
 * float disconnected out on the axis. The extension is still bounded by the
 * current multiple itself — nothing is extrapolated past a real, plotted x.
 */
const buildFitLineData = (points, slope, intercept, currentMultiple) => {
  if (!points.length) return [];
  const xs = points.map((p) => p.forwardMultiple);
  if (currentMultiple != null) xs.push(currentMultiple);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  return [
    [minX, (slope * minX + intercept) * 100],
    [maxX, (slope * maxX + intercept) * 100],
  ];
};

/**
 * The "today" marker: a red diamond at (currentMultiple, projectedReturn), a
 * dashed vertical drop-line from the x-axis up to it, and a callout
 * (data label) attached to the marker showing both values. Returns `[]`
 * (nothing rendered) when either value is unavailable — the caller must
 * never fabricate a marker at (0, 0).
 */
const buildCurrentPointSeries = (currentMultiple, projectedReturn, xLabel, formatX, points) => {
  if (currentMultiple == null || projectedReturn == null) return [];
  const y = projectedReturn * 100;

  // Anchor the callout away from whichever edge this point is closest to,
  // based on the actual plotted data's range, instead of always
  // left/middle. The "current" point is often near the high end of the
  // historical multiple range, which pushes a fixed left-aligned callout
  // off the chart's right edge — far more likely to clip on a narrow
  // mobile chart than a wide desktop one, since there's less slack space
  // to begin with.
  const xs = points.map((p) => p.forwardMultiple);
  const ys = points.map((p) => p.subsequentReturn * 100);
  const minX = xs.length ? Math.min(...xs, currentMultiple) : currentMultiple;
  const maxX = xs.length ? Math.max(...xs, currentMultiple) : currentMultiple;
  const minY = ys.length ? Math.min(...ys, y) : y;
  const maxY = ys.length ? Math.max(...ys, y) : y;
  const xSpan = maxX - minX || 1;
  const ySpan = maxY - minY || 1;
  const nearRightEdge = (currentMultiple - minX) / xSpan > 0.6;
  const nearTopEdge = (y - minY) / ySpan > 0.75;

  const labelAlign = nearRightEdge ? 'right' : 'left';
  const labelX = nearRightEdge ? -10 : 10;
  const labelVerticalAlign = nearTopEdge ? 'bottom' : 'middle';
  const labelY = nearTopEdge ? 8 : -8;

  return [
    {
      id: 'current-multiple-dropline',
      name: `Current ${xLabel}`,
      type: 'line',
      data: [[currentMultiple, 0], [currentMultiple, y]],
      color: CURRENT_POINT_COLOR,
      dashStyle: 'Dash',
      lineWidth: 1.5,
      marker: { enabled: false },
      enableMouseTracking: false,
      showInLegend: false,
    },
    {
      id: 'current-multiple-marker',
      name: `Current ${xLabel}`,
      type: 'scatter',
      data: [{ x: currentMultiple, y }],
      color: CURRENT_POINT_COLOR,
      marker: {
        symbol: 'diamond',
        radius: 6,
        lineColor: '#ffffff',
        lineWidth: 1,
      },
      enableMouseTracking: false,
      showInLegend: false,
      zIndex: 10,
      dataLabels: {
        enabled: true,
        useHTML: true,
        align: labelAlign,
        verticalAlign: labelVerticalAlign,
        x: labelX,
        y: labelY,
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        borderColor: CURRENT_POINT_COLOR,
        borderWidth: 1,
        borderRadius: 4,
        padding: 6,
        style: { fontSize: '11px', fontWeight: 'normal', color: '#2c3e50', textOutline: 'none' },
        formatter: function () {
          return `Current ${xLabel}: ${formatX(currentMultiple)}<br/>Implied Return: ${y.toFixed(1)}% p.a.`;
        },
      },
    },
  ];
};

/**
 * Builds the full chart options for one horizon's regression result
 * (`{ points, slope, intercept, currentMultiple, projectedReturn, period }`,
 * i.e. one value of `resultsByHorizon` — `period` is echoed back by
 * `computeValuationRegression` itself, so callers spreading that result
 * wholesale get it for free), plus the currently-selected `metric` — used
 * only to derive the human-readable multiple label (e.g. "P/E", "P/S", or,
 * under Enterprise Value, "EV/E", "EV/S") via `ratioName`/`yieldName`, keyed
 * by the same `valueBasis` the backtest math itself uses — so the label and
 * the numerator can never disagree (P/E label over EV math, or vice versa).
 * `valueBasis` is echoed back by `computeValuationRegression`, so callers
 * spreading that result get it for free. Passed wholesale to
 * `HighchartsReact`'s `options` prop; switching horizons/metric/basis just
 * calls this again with a different result, and `HighchartsReact`'s default
 * `oneToOne` update animates the series transition.
 */
export const getValuationChartOptions = ({
  points, slope, intercept, currentMultiple, projectedReturn, metric, period = 'ntm', valueBasis = 'marketCap',
}) => {
  // Dividends is plotted as a yield (a fraction), every other metric as a
  // forward/trailing multiple (a P/E-style "Nx"). This flips the axis
  // label, the scatter/current-point value formatting, and the methodology
  // caption's "Forward X"/"Trailing X" phrasing — see metricsConfig's
  // isYieldMetric. A yield has no forward/trailing distinction worth
  // surfacing in its label (it's still just "Dividend Yield" either way),
  // so `period` only affects the multiple-form xLabel below.
  const asYield = isYieldMetric(metric);
  const xLabel = asYield
    ? yieldName(metric, valueBasis)
    : `${period === 'ttm' ? 'Trailing' : 'Forward'} ${ratioName(metric, valueBasis)}`;
  const formatX = asYield
    ? (v) => `${(v * 100).toFixed(2)}%`
    : (v) => `${v.toFixed(1)}x`;
  // Yield metrics' x-values are raw fractions (0.03 = 3%), so the axis TICK
  // labels need explicit percent formatting — otherwise Highcharts prints
  // the bare fraction. Multiple metrics keep Highcharts' default numeric
  // tick labels (a plain "15", "20", … on the Nx scale). Dividend yields
  // cluster in a narrow band (e.g. all within 1–2%), so whole-number percent
  // ticks collapse to a run of identical "1%"/"2%" labels; two decimals
  // (e.g. "1.35%") keep the ticks distinguishable and match the per-point
  // tooltip/callout formatting via `formatX`. Because this whole factory
  // re-runs on every metric switch, the axis reverts to the default
  // formatter automatically when a non-yield metric is picked.
  const xAxisLabels = asYield
    ? { formatter: function () { return `${(this.value * 100).toFixed(2)}%`; } }
    : {};

  return {
    chart: {
      type: 'scatter',
      animation: true,
      height: 380,
    },
    title: { text: undefined },
    credits: { enabled: false },
    // The realized-vs-consensus methodology caveat that used to live here as a
    // chart caption now lives in the page-bottom disclaimer footer (footnote
    // referenced by the correlation-table legend's DisclaimerMarker
    // id="ntm-realized" in ValuationSubsequentReturnsCard.js), so the chart
    // frame itself stays clean. See config/disclaimers.js.
    xAxis: {
      title: { text: xLabel },
      labels: xAxisLabels,
      crosshair: false,
    },
    yAxis: {
      title: { text: 'Subsequent Return (% p.a.)' },
      labels: { format: '{value}%' },
      // A stronger reference line at the 0% break-even boundary — as visually
      // prominent as a normal axis line, not just another faint gridline —
      // since this is the one value on this axis a viewer actually needs to
      // scan for (profit vs. loss), unlike an arbitrary gridline increment.
      plotLines: [{
        value: 0,
        color: '#4a5568',
        width: 1.5,
        zIndex: 3,
      }],
    },
    legend: { enabled: false },
    tooltip: { useHTML: true },
    plotOptions: {
      series: {
        states: { inactive: { enabled: false } },
      },
    },
    responsive: {
      rules: [
        {
          // Portrait phones: this card's fixed 380px height is otherwise
          // unresponsive to width, so a narrow chart ends up taller
          // (relative to its width) than it needs to be. Bump the scatter
          // marker radius up slightly too — 3.5px circles are a tight tap
          // target on a touchscreen.
          condition: { maxWidth: 480 },
          chartOptions: {
            chart: { height: 300 },
            // Spread `xAxisLabels` first so a yield metric's percent
            // `formatter` survives this mobile override (which only means to
            // shrink the font, not replace the label formatting).
            xAxis: { title: { style: { fontSize: '11px' } }, labels: { ...xAxisLabels, style: { fontSize: '10px' } } },
            yAxis: { title: { style: { fontSize: '11px' } }, labels: { style: { fontSize: '10px' } } },
            // Every series that should survive this rule must be listed
            // here by `id`, even ones with nothing to change — Highcharts'
            // responsive engine always applies `chartOptions.series` via
            // `chart.update(mergedOptions, redraw, /* oneToOne */ true)`
            // (see Highcharts' Responsive.setResponsive), and `oneToOne`
            // update removes any existing series it doesn't find a match
            // for in the new array, REGARDLESS of whether the ones it does
            // list are id- or position-matched. With only the scatter
            // series (`valuation-history`) listed, every other series —
            // the regression-fit line, the "today" drop-line, the "today"
            // marker (see the full `series` array below) — was being
            // silently removed from the chart on every mobile-width
            // mount/resize, which is exactly the "fit line and today point
            // don't render in portrait" bug this fixes (confirmed via
            // real-browser Playwright: 0 series groups beyond the scatter
            // one at <=480px, 4 at wider widths where this rule doesn't
            // apply). Listing the other three by id alone (no style change)
            // is enough to mark them "touched" and keep them; `id`-only
            // entries for series that happen not to exist yet (e.g. no
            // current-multiple point) are harmless no-ops.
            series: [
              { id: 'valuation-history', marker: { radius: 4.5 } },
              { id: 'regression-fit' },
              { id: 'current-multiple-dropline' },
              { id: 'current-multiple-marker' },
            ],
          },
        },
        {
          // Landscape phones / short viewports — see chartConfig.js's
          // isShortLandscapeViewport docstring for why width alone isn't
          // enough of a signal here.
          condition: {
            minWidth: 481,
            maxWidth: 1024,
            callback: function () { return isShortLandscapeViewport(); },
          },
          chartOptions: {
            chart: { height: 220 },
          },
        },
      ],
    },
    series: [
      {
        id: 'valuation-history',
        name: `Historical ${xLabel} vs. subsequent return`,
        type: 'scatter',
        data: points.map((p) => ({ x: p.forwardMultiple, y: p.subsequentReturn * 100, date: p.date })),
        color: MARKER_COLOR,
        marker: {
          symbol: 'circle',
          radius: 3.5,
          fillOpacity: 0.45,
          fillColor: 'rgba(74, 144, 226, 0.45)',
          lineWidth: 0,
        },
        tooltip: {
          pointFormatter: function () {
            return `${this.date}<br/>${xLabel}: ${formatX(this.x)}<br/>Subsequent Return: ${this.y.toFixed(1)}% p.a.`;
          },
        },
      },
      {
        id: 'regression-fit',
        name: 'Regression fit',
        type: 'line',
        data: buildFitLineData(points, slope, intercept, currentMultiple),
        color: '#2c3e50',
        lineWidth: 2,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
      },
      ...buildCurrentPointSeries(currentMultiple, projectedReturn, xLabel, formatX, points),
    ],
  };
};

export default getValuationChartOptions;
